import type { ExperienceDto } from "@intelligems/ig-types/dtos/experiences";
import { useEffect, useRef } from "react";

import {
  GetExperiencesResponseBodyType,
  GetExperiencesType,
} from "@intelligems/ig-types/functions/experiences/get-experiences";
import { api } from "@src/api/api";
import { getNextPageParam } from "@src/api/getNextPageParam";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

export const getExperiences = async (options: {
  type?: GetExperiencesType;
  signal?: AbortSignal;
  page?: number;
  limit?: number;
}) => {
  const resp = await api.get<GetExperiencesResponseBodyType>(
    `/v2/experiences`,
    {
      signal: options.signal,
      params: {
        page: options.page,
        limit: options.limit,
        type: options.type,
      },
    }
  );
  return normalizeExperiencesPage(resp.data);
};

/**
 * Normalize a page before it enters the react-query cache: a malformed/empty
 * backend payload can arrive without the `experiences` array, and cache
 * updaters (updateInExperiencesCache & co in queryKeys.ts) map/filter/spread
 * `page.experiences` unconditionally on later mutations.
 */
export const normalizeExperiencesPage = (
  page: GetExperiencesResponseBodyType
): GetExperiencesResponseBodyType => ({
  ...page,
  experiences: page.experiences ?? [],
});

export const getExperiencesNextPageParam = (
  lastPage: GetExperiencesResponseBodyType,
  limit: number
): number | undefined =>
  getNextPageParam(lastPage.experiences, lastPage.page, limit);

export const useGetExperiences = (type: GetExperiencesType) => {
  const limit = 50;
  const fetchingRef = useRef(false);

  const experiences = useInfiniteQuery<
    GetExperiencesResponseBodyType,
    Error,
    GetExperiencesResponseBodyType
  >({
    initialPageParam: 1,
    queryKey: [{ scope: `experiences-${type}` }],
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) =>
      getExperiences({ type, page: pageParam as number, limit }),
    getNextPageParam: (lastPage) =>
      getExperiencesNextPageParam(lastPage, limit),
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page && firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data.pages,
        pageParams: data.pageParams,
        experiences: data.pages.flatMap((page) => page.experiences ?? []),
      };
    },
  });

  useEffect(() => {
    if (
      experiences.hasNextPage &&
      !experiences.isFetchingNextPage &&
      !fetchingRef.current
    ) {
      fetchingRef.current = true;
      void experiences.fetchNextPage().finally(() => {
        fetchingRef.current = false;
      });
    }
  }, [experiences.hasNextPage, experiences.isFetchingNextPage]);

  return experiences;
};

export async function getSupportExperiences(
  signal: AbortSignal,
  getPage: (
    page: number,
    signal: AbortSignal
  ) => Promise<GetExperiencesResponseBodyType>
): Promise<ExperienceDto[]> {
  const experiences: ExperienceDto[] = [];
  for (let page = 1; ; page++) {
    const result = await getPage(page, signal);
    signal.throwIfAborted();
    const batch = result.experiences ?? [];
    experiences.push(...batch);
    if (
      getExperiencesNextPageParam(
        { ...result, experiences: batch, page },
        100
      ) === undefined
    ) {
      return experiences;
    }
  }
}

export function useSupportExperiences(orgId: string) {
  return useQuery({
    queryKey: [{ scope: "support-experiences", orgId }],
    queryFn: ({ signal }) =>
      getSupportExperiences(signal, (page, signal) =>
        getExperiences({ page, limit: 100, signal })
      ),
  });
}
